import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Store, Plus, Trash2, Pencil, Save, X, Eye, EyeOff, Loader2, RefreshCw,
  ClipboardList, Package, Truck, Wine, Palette, CheckCircle2, Key, Copy, Upload, Image as ImageIcon,
  ArrowUp, ArrowDown, ListOrdered, ImageDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function client() {
  const tok = localStorage.getItem("admin_token");
  return axios.create({ baseURL: API, headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
}
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function ShopEditor() {
  const [sub, setSub] = useState("orders");
  return (
    <div className="space-y-4" data-testid="shop-editor">
      <header className="rounded-2xl bg-gradient-to-br from-[#2b1410] to-[#4a1c17] text-[#f7ebdf] p-5 flex flex-wrap items-center gap-4">
        <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center">
          <Store className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-2xl font-bold">Shop</h2>
          <p className="text-sm text-[#f7ebdf]/75 mt-0.5">Vino · olio · giardiniera · quadri — spedizione 8/15€ (gratis >150€)</p>
        </div>
      </header>

      <nav className="flex gap-1 border-b border-[#e8dcc8] overflow-x-auto">
        <Tab active={sub === "orders"} onClick={() => setSub("orders")} icon={<ClipboardList className="h-4 w-4" />}>Ordini</Tab>
        <Tab active={sub === "products"} onClick={() => setSub("products")} icon={<Package className="h-4 w-4" />}>Prodotti</Tab>
        <Tab active={sub === "runner"} onClick={() => setSub("runner")} icon={<Truck className="h-4 w-4" />}>Fattorino</Tab>
      </nav>

      {sub === "orders" ? <OrdersPanel /> : sub === "products" ? <ProductsPanel /> : <RunnerPanel />}
    </div>
  );
}

function Tab({ active, onClick, icon, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition whitespace-nowrap
        ${active ? "border-[#4a1c17] text-[#4a1c17]" : "border-transparent text-[#7c5a4e] hover:text-[#2b1410]"}`}>
      {icon}{children}
    </button>
  );
}

// -------------------- Orders --------------------
function OrdersPanel() {
  const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await client().get("/shop/admin/orders"); setOrders(data || []); }
    catch { toast.error("Errore caricamento"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await client().patch(`/shop/admin/orders/${id}`, { status }); load(); }
    catch { toast.error("Errore aggiornamento"); }
  };
  const removeOrder = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo ordine annullato?")) return;
    try { await client().delete(`/shop/admin/orders/${id}`); load(); } catch { toast.error("Errore eliminazione"); }
  };
  const purgeCancelled = async () => {
    const n = orders.filter((o) => o.status === "cancelled").length;
    if (!window.confirm(`Eliminare definitivamente tutti i ${n} ordini annullati?`)) return;
    try { const r = await client().delete("/shop/admin/orders/cancelled"); toast.success(`${r.data.deleted} ordini annullati eliminati`); load(); } catch { toast.error("Errore eliminazione"); }
  };
  const markPaid = async (id) => {
    try { await client().patch(`/shop/admin/orders/${id}`, { payment_status: "paid" }); toast.success("Ordine segnato come pagato"); load(); }
    catch { toast.error("Errore aggiornamento pagamento"); }
  };

  return (
    <div>
      <div className="mb-2 flex justify-end gap-2">
        {orders.some((o) => o.status === "cancelled") && <button onClick={purgeCancelled} className="rounded-full border border-red-300 text-red-700 px-3 py-1 text-xs inline-flex items-center gap-1" data-testid="shop-purge-cancelled"><Trash2 className="h-3.5 w-3.5" /> Elimina annullati ({orders.filter((o) => o.status === "cancelled").length})</button>}
        <button onClick={load} className="rounded-full border border-[#e8dcc8] px-3 py-1 text-xs inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</button>
      </div>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
        : orders.length === 0 ? <div className="rounded-xl bg-white border border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">Nessun ordine.</div>
        : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="rounded-xl bg-white border border-[#e8dcc8] p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[10px] font-mono text-[#7c5a4e]">#{o.id.slice(0,8)}</div>
                  <div className="font-semibold text-[#2b1410]">{o.name}</div>
                  <span className="text-xs text-[#7c5a4e]">{o.mode === "pickup" ? "Ritiro" : o.mode === "shipping" ? "Spedizione" : "Consegna nostra"}</span>
                  <span className="text-xs text-[#7c5a4e]">·</span>
                  <span className="text-xs text-[#7c5a4e]">{o.items.length} pezzi · {eur(o.total)}</span>
                  {o.payment_status === "paid" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  <span className={`ml-auto text-[10px] uppercase font-semibold rounded-full px-2 py-0.5
                    ${o.status === "confirmed" ? "bg-blue-100 text-blue-800" : o.status === "shipped" ? "bg-emerald-100 text-emerald-800" : o.status === "delivered" ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-amber-100 text-amber-800"}`}>
                    {o.status}
                  </span>
                </div>
                <div className="mt-2 text-xs text-[#7c5a4e]">
                  <a href={`tel:${o.phone}`} className="text-[#4a1c17] underline">{o.phone}</a>
                  {o.address && <span> · {o.address}</span>}
                </div>
                <div className="mt-1 text-xs text-[#7c5a4e]">{o.items.map((it, i) => <span key={i}>{it.qty}× {it.name}{i < o.items.length - 1 ? " · " : ""}</span>)}</div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {o.status === "pending" && <button onClick={() => setStatus(o.id, "confirmed")} className="text-xs rounded-full bg-[#4a1c17] text-[#f7ebdf] px-3 py-1">Conferma</button>}
                  {o.status === "confirmed" && o.mode === "shipping" && <button onClick={() => setStatus(o.id, "shipped")} className="text-xs rounded-full bg-emerald-600 text-white px-3 py-1">Spedito</button>}
                  {o.status === "confirmed" && o.mode !== "shipping" && <button onClick={() => setStatus(o.id, "delivered")} className="text-xs rounded-full bg-emerald-600 text-white px-3 py-1">Consegnato</button>}
                  {o.payment_status !== "paid" && ["cash","pos"].includes(o.payment_method) && (
                    <button onClick={() => markPaid(o.id)} data-testid={`shop-mark-paid-${o.id}`} className="text-xs rounded-full border border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-1 font-semibold">Segna pagato</button>
                  )}
                  {["pending","confirmed"].includes(o.status) && <button onClick={() => setStatus(o.id, "cancelled")} className="text-xs rounded-full border border-red-300 text-red-700 px-3 py-1">Annulla</button>}
                  {o.status === "cancelled" && <button onClick={() => removeOrder(o.id)} className="text-xs rounded-full border border-red-300 text-red-700 px-3 py-1 inline-flex items-center gap-1" data-testid={`shop-order-delete-${o.id}`}><Trash2 className="h-3 w-3" /> Elimina</button>}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// -------------------- Products --------------------
function ProductsPanel() {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true); const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [orderOpen, setOrderOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await client().get("/shop/admin/items"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!confirm("Eliminare?")) return; try { await client().delete(`/shop/admin/items/${id}`); load(); } catch { toast.error("Errore"); } };
  const toggle = async (it) => { try { await client().put(`/shop/admin/items/${it.id}`, { ...it, active: !it.active }); load(); } catch { toast.error("Errore"); } };

  // Derive the category list from actual products, keeping the order they
  // appear (admin can rely on the sort_order they've set).
  const categories = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const it of items) {
      const cat = (it.category || "").trim();
      if (cat && !seen.has(cat)) { seen.add(cat); out.push(cat); }
    }
    return out;
  }, [items]);

  const filtered = filter === "all" ? items : items.filter((it) => (it.category || "").trim() === filter);
  const countBy = (cat) => items.filter((it) => (it.category || "").trim() === cat).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setEditing("new")} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Aggiungi prodotto</button>
        <button onClick={() => setOrderOpen((v) => !v)} data-testid="shop-order-toggle" className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-[#faf3ee]">
          <ListOrdered className="h-3.5 w-3.5" /> Ordine categorie
        </button>
        <CompressImagesButton onDone={load} />
        <button onClick={load} className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</button>
      </div>
      {orderOpen && <CategoryOrderPanel onClose={() => setOrderOpen(false)} onSaved={load} />}
      {categories.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="shop-filter-pills">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} label={`Tutti · ${items.length}`} />
          {categories.map((c) => (
            <FilterPill key={c} active={filter === c} onClick={() => setFilter(c)} label={`${c} · ${countBy(c)}`} testId={`shop-filter-${c}`} />
          ))}
        </div>
      )}
      {editing === "new" && <ProductForm onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
        : filtered.length === 0 ? <div className="rounded-xl bg-white border border-[#e8dcc8] p-10 text-center text-sm text-[#7c5a4e]">
            {filter === "all" ? "Nessun prodotto — aggiungi il primo." : `Nessun prodotto nella categoria "${filter}".`}
          </div>
        : (
          <div className="space-y-1.5">
            {filtered.map((it) => editing === it.id
              ? <ProductForm key={it.id} item={it} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
              : (
                <div key={it.id} className="rounded-xl bg-white border border-[#e8dcc8] p-3 flex items-center gap-3">
                  {it.image
                    ? <img src={it.image} alt="" className={it.kind === "quadro" ? "h-9 w-16 rounded-lg object-cover bg-[#2b1410]" : "h-12 w-12 rounded-lg object-cover"} />
                    : <div className={it.kind === "quadro" ? "h-9 w-16 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] grid place-items-center text-[10px] text-[#7c5a4e]" : "h-12 w-12 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] grid place-items-center text-[10px] text-[#7c5a4e]"}>FOTO</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase text-[#7c5a4e]">
                      {it.category}
                      {" · "}
                      {it.kind === "quadro"
                        ? [it.art_type, it.dimensions].filter(Boolean).join(" · ") || "PEZZO UNICO"
                        : (it.format || `${it.weight_kg || 0}kg`)}
                    </div>
                    <div className="font-semibold text-[#2b1410] truncate">{it.name}</div>
                  </div>
                  <div className="text-sm font-bold text-[#4a1c17]">{eur(it.price)}</div>
                  {it.kind === "quadro" && <span className={`text-[10px] rounded-full px-2 py-0.5 ${it.stock === 0 ? "bg-red-100 text-red-700" : "bg-purple-100 text-purple-800"}`}>{it.stock === 0 ? "Venduto" : "Disponibile"}</span>}
                  <button onClick={() => toggle(it)} className={`p-1.5 rounded-full ${it.active ? "text-emerald-700" : "text-[#7c5a4e]"} hover:bg-[#f7ebdf]`} title={it.active ? "Visibile" : "Nascosto"}>
                    {it.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button onClick={() => setEditing(it.id)} className="p-1.5 rounded-full hover:bg-[#f7ebdf]"><Pencil className="h-4 w-4 text-[#4a1c17]" /></button>
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded-full hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-600" /></button>
                </div>
              ))}
          </div>
        )}
    </div>
  );
}

function ProductForm({ item, onSaved, onCancel }) {
  const [f, setF] = useState(item || {
    category: "Vino", name: "", description: "", price: "", image: "",
    weight_kg: 1.2, format: "75 cl",
    stock: -1, kind: "regular", no_shipping: false, active: true, sort_order: 0,
    art_type: "", dimensions: "",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!item;
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const setKind = (k) => setF((s) => ({
    ...s, kind: k,
    no_shipping: k === "quadro" ? true : false,
    stock: k === "quadro" ? 1 : -1,
    weight_kg: k === "quadro" ? 0 : s.weight_kg,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category.trim() || !f.name.trim() || !f.price) { toast.error("Campi obbligatori mancanti"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f, price: Number(f.price), weight_kg: Number(f.weight_kg) || 0,
        stock: Number(f.stock), sort_order: Number(f.sort_order) || 0,
      };
      if (isEdit) await client().put(`/shop/admin/items/${item.id}`, payload);
      else await client().post("/shop/admin/items", payload);
      toast.success(isEdit ? "Prodotto salvato" : "Prodotto aggiunto");
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl bg-[#fbf6ef] border border-[#e8dcc8] p-4 space-y-3">
      <div className="flex gap-2">
        <KindBtn active={f.kind === "regular"} onClick={() => setKind("regular")} icon={Wine} label="Prodotto regolare" />
        <KindBtn active={f.kind === "quadro"} onClick={() => setKind("quadro")} icon={Palette} label="Quadro (pezzo unico)" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Categoria *"><input required value={f.category} onChange={on("category")} placeholder={f.kind === "quadro" ? "Quadri" : "Vino / Olio / Giardiniera"} className="sh-input" /></Field>
        <Field label="Prezzo € *"><input required type="number" step="0.5" value={f.price} onChange={on("price")} className="sh-input" /></Field>
        <Field label={f.kind === "quadro" ? "Nome opera *" : "Nome *"}>
          <input required value={f.name} onChange={on("name")} placeholder={f.kind === "quadro" ? "Es. Tramonto sul Pozzo" : ""} className="sh-input" />
        </Field>
        {f.kind === "quadro" ? (
          <Field label="Stock (1 = disponibile · 0 = venduto)">
            <input type="number" min="0" max="1" value={f.stock} onChange={on("stock")} className="sh-input" />
          </Field>
        ) : (
          <>
            <Field label="Formato">
              <input value={f.format} onChange={on("format")} placeholder="Es. 75 cl · 500 ml · 500 g" className="sh-input" />
            </Field>
            <Field label="Peso spedizione (kg)">
              <input type="number" step="0.1" value={f.weight_kg} onChange={on("weight_kg")} className="sh-input" />
              <div className="text-[10px] text-[#7c5a4e] normal-case tracking-normal mt-0.5">
                Serve solo al calcolo automatico della spedizione (8€ &lt; 5kg · 15€ ≥ 5kg). Non lo vede il cliente.
              </div>
            </Field>
          </>
        )}
        {f.kind === "quadro" && (
          <>
            <Field label="Tipo opera">
              <input value={f.art_type} onChange={on("art_type")} placeholder="Es. Olio su tela · Acquerello · Acrilico su tavola" className="sh-input" />
            </Field>
            <Field label="Dimensioni">
              <input value={f.dimensions} onChange={on("dimensions")} placeholder="Es. 60 x 40 cm" className="sh-input" />
            </Field>
          </>
        )}
        <Field label={f.kind === "quadro" ? "Descrizione / storia dell'opera" : "Descrizione"} className="sm:col-span-2">
          <input value={f.description} onChange={on("description")} className="sh-input" />
        </Field>
        <Field label="Foto prodotto" className="sm:col-span-2">
          <ImageUploader value={f.image} onChange={(url) => setF((v) => ({ ...v, image: url }))} kind={f.kind} />
        </Field>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-full border border-[#e8dcc8] bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Annulla</button>
        <button type="submit" disabled={saving} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva
        </button>
      </div>
    </form>
  );
}

function KindBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex-1 rounded-lg border-2 p-2 text-left transition ${active ? "border-[#4a1c17] bg-[#faf3ee]" : "border-[#e8dcc8] bg-white"}`}>
      <Icon className={`h-4 w-4 ${active ? "text-[#4a1c17]" : "text-[#7c5a4e]"}`} />
      <div className="text-xs font-semibold mt-1">{label}</div>
    </button>
  );
}

// -------------------- Runner config --------------------
function RunnerPanel() {
  const [status, setStatus] = useState({ configured: false });
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const url = `${window.location.origin}/runner`;

  const load = async () => {
    try { const { data } = await client().get("/runner/admin/status"); setStatus(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (pwd.length < 4) { toast.error("Almeno 4 caratteri"); return; }
    setSaving(true);
    try { await client().put("/runner/admin/password", { password: pwd }); toast.success("Password fattorino aggiornata"); setPwd(""); load(); }
    catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-5 space-y-4 max-w-xl">
      <div className="flex items-center gap-3">
        <Truck className="h-6 w-6 text-[#4a1c17]" />
        <div>
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">Accesso Fattorino</h3>
          <p className="text-xs text-[#7c5a4e]">Il ragazzo delle consegne accede da <b>/runner</b> con solo la lista delle consegne del giorno.</p>
        </div>
      </div>

      <div className="text-sm">
        Stato: {status.configured
          ? <span className="text-emerald-700 font-semibold">✓ Password impostata</span>
          : <span className="text-amber-700 font-semibold">Non ancora configurato</span>}
      </div>

      <div className="rounded-lg bg-[#faf3ee] border border-[#e8dcc8] p-3">
        <div className="text-[11px] uppercase font-semibold text-[#7c5a4e] mb-1 flex items-center gap-1"><Key className="h-3 w-3" /> URL da inviare al fattorino</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate text-xs bg-white border border-[#e8dcc8] rounded px-2 py-1">{url}</code>
          <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("URL copiato"); }} className="rounded-full border border-[#e8dcc8] bg-white px-2 py-1 text-xs inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copia</button>
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">
          {status.configured ? "Aggiorna password" : "Imposta password"}
        </label>
        <div className="flex gap-2">
          <input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Minimo 4 caratteri" data-testid="runner-admin-pwd" className="sh-input flex-1" />
          <button onClick={save} disabled={saving || pwd.length < 4} className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold disabled:opacity-40">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <label className={`block text-[11px] uppercase tracking-wide font-semibold text-[#7c5a4e] ${className}`}>{label}<div className="mt-1">{children}</div></label>;
}

function FilterPill({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 rounded-full text-[11px] font-semibold transition ${
        active
          ? "bg-[#4a1c17] text-[#f7ebdf] shadow"
          : "bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]"
      }`}
    >
      {label}
    </button>
  );
}

function ImageUploader({ value, onChange, kind = "regular" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const isQuadro = kind === "quadro";
  const boxCls = isQuadro
    ? "w-32 aspect-video rounded-lg object-cover border border-[#e8dcc8] bg-[#2b1410]"
    : "h-20 w-20 rounded-lg object-cover border border-[#e8dcc8]";
  const placeholderCls = isQuadro
    ? "w-32 aspect-video rounded-lg border-2 border-dashed border-[#e8dcc8] flex items-center justify-center text-[#c8b8a6]"
    : "h-20 w-20 rounded-lg border-2 border-dashed border-[#e8dcc8] flex items-center justify-center text-[#c8b8a6]";
  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Immagine troppo grande (max 3MB). Prova a comprimerla."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client().post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url;
      if (!url) throw new Error("URL non ricevuto dal server");
      onChange(url);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload fallito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative">
          <img src={value} alt="" className={boxCls} />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid="shop-image-remove"
            className="absolute -top-2 -right-2 rounded-full bg-white shadow border border-[#e8dcc8] p-1 hover:bg-red-50"
            title="Rimuovi foto"
          >
            <X className="h-3 w-3 text-red-600" />
          </button>
        </div>
      ) : (
        <div className={placeholderCls}>
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
      <div className="flex-1 space-y-1.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="shop-image-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="shop-image-upload"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {value ? "Cambia foto" : "Carica foto"}
        </button>
        <p className="text-[10px] text-[#7c5a4e] normal-case tracking-normal">
          {isQuadro
            ? "JPG · max 3 MB · solo l'opera senza cornice · orizzontale 16:9"
            : "JPG o PNG · max 3 MB · consigliato quadrato 800×800"}
        </p>
      </div>
    </div>
  );
}

// -------------------- Category order --------------------
function CategoryOrderPanel({ onClose, onSaved }) {
  const [order, setOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client().get("/shop/admin/category-order");
      setOrder(data.order || []);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = (idx, dir) => {
    const next = [...order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await client().put("/shop/admin/category-order", { order });
      toast.success("Ordine categorie salvato");
      onSaved?.();
      onClose?.();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4" data-testid="shop-category-order-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-[#2b1410] inline-flex items-center gap-1.5"><ListOrdered className="h-4 w-4" /> Ordine categorie</h3>
          <p className="text-[11px] text-[#7c5a4e] mt-0.5">Trascina in alto le categorie da mostrare per prime nel filtro dello Shop.</p>
        </div>
        <button onClick={onClose} className="text-[#7c5a4e] hover:text-[#4a1c17]"><X className="h-4 w-4" /></button>
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#4a1c17]" /></div>
      ) : order.length === 0 ? (
        <div className="text-xs text-[#7c5a4e] py-4 text-center">Nessuna categoria ancora — aggiungi un prodotto prima.</div>
      ) : (
        <ul className="space-y-1.5">
          {order.map((cat, idx) => (
            <li key={cat} className="flex items-center gap-2 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] px-3 py-2">
              <span className="text-[11px] text-[#7c5a4e] font-mono w-5">{idx + 1}.</span>
              <span className="flex-1 text-sm font-medium text-[#2b1410]">{cat}</span>
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="h-7 w-7 grid place-items-center rounded-full border border-[#e8dcc8] bg-white text-[#4a1c17] disabled:opacity-30" data-testid={`shop-cat-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === order.length - 1} className="h-7 w-7 grid place-items-center rounded-full border border-[#e8dcc8] bg-white text-[#4a1c17] disabled:opacity-30" data-testid={`shop-cat-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs">Annulla</button>
        <button onClick={save} disabled={saving || loading} data-testid="shop-cat-order-save" className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva ordine
        </button>
      </div>
    </div>
  );
}


const _sty = `.sh-input { display:block; width:100%; padding:.5rem .75rem; border-radius:.375rem; border:1px solid #e8dcc8; background:white; font-size:.875rem; outline:none; } .sh-input:focus { box-shadow:0 0 0 2px rgba(74,28,23,.25); border-color:#4a1c17; }`;
if (typeof document !== "undefined" && !document.getElementById("shop-admin-style")) {
  const s = document.createElement("style"); s.id = "shop-admin-style"; s.textContent = _sty; document.head.appendChild(s);
}


// -------------------- Compress images --------------------
function CompressImagesButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);

  const dryRun = async () => {
    setRunning(true);
    try {
      const { data } = await client().post("/shop/admin/compress-images?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore analisi");
    } finally { setRunning(false); }
  };

  const apply = async () => {
    if (!confirm("Applicare la compressione a tutte le immagini? Le originali verranno sostituite.")) return;
    setRunning(true);
    try {
      const { data } = await client().post("/shop/admin/compress-images?dry_run=false");
      toast.success(`Compresse ${data.processed} immagini · risparmiati ${(data.total_saved_kb / 1024).toFixed(1)} MB (${data.saved_pct}%)`);
      setPreview(null);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore compressione");
    } finally { setRunning(false); }
  };

  return (
    <>
      <button
        onClick={dryRun}
        disabled={running}
        data-testid="shop-compress-btn"
        className="rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-[#faf3ee] disabled:opacity-50"
        title="Riduce il peso delle foto shop convertendole in JPEG max 1400px"
      >
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageDown className="h-3.5 w-3.5" />} Comprimi foto
      </button>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#e8dcc8] flex items-start justify-between">
              <div>
                <h3 className="font-bold text-[#2b1410] text-base">Anteprima compressione</h3>
                <p className="text-xs text-[#7c5a4e] mt-0.5">
                  {preview.processed} immagini · prima <strong>{(preview.total_before_kb/1024).toFixed(1)} MB</strong> → dopo <strong className="text-emerald-700">{(preview.total_after_kb/1024).toFixed(1)} MB</strong> (risparmio <strong className="text-emerald-700">{preview.saved_pct}%</strong>)
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="text-[#7c5a4e]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-xs">
                <thead className="text-[#7c5a4e] text-left border-b border-[#e8dcc8]">
                  <tr><th className="py-1">Prodotto</th><th className="py-1 text-right">Prima</th><th className="py-1 text-right">Dopo</th><th className="py-1 text-right">-%</th></tr>
                </thead>
                <tbody>
                  {preview.items.map((it, i) => (
                    <tr key={i} className="border-b border-[#f0e5d5]">
                      <td className="py-1 truncate max-w-[240px]">{it.name}</td>
                      {it.error ? <td colSpan={3} className="py-1 text-red-600">{it.error}</td> : <>
                        <td className="py-1 text-right text-[#7c5a4e]">{it.before_kb.toFixed(0)} KB</td>
                        <td className="py-1 text-right font-semibold">{it.after_kb.toFixed(0)} KB</td>
                        <td className="py-1 text-right text-emerald-700">-{it.saved_pct}%</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-[#e8dcc8] flex justify-end gap-2">
              <button onClick={() => setPreview(null)} className="rounded-full border border-[#e8dcc8] px-4 py-2 text-xs">Annulla</button>
              <button onClick={apply} disabled={running} data-testid="shop-compress-apply" className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Applica compressione
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
